import { addDoc, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { db } from '../firebase'

const SESSION_STORAGE_KEY = 'ubunifu_instructor_activity_session'
const LOGIN_TRACK_STORAGE_KEY = 'ubunifu_instructor_activity_login'

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
}

function createSessionId(userId = 'instructor') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${userId}-${crypto.randomUUID()}`
  }

  return `${userId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function readSessionState() {
  if (!canUseSessionStorage()) return null

  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    console.error('Unable to read instructor activity session state:', error)
    return null
  }
}

function writeSessionState(state) {
  if (!canUseSessionStorage()) return

  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('Unable to persist instructor activity session state:', error)
  }
}

function readLoginState() {
  if (!canUseSessionStorage()) return null

  try {
    return window.sessionStorage.getItem(LOGIN_TRACK_STORAGE_KEY)
  } catch (error) {
    console.error('Unable to read instructor login tracking state:', error)
    return null
  }
}

function writeLoginState(sessionId) {
  if (!canUseSessionStorage()) return

  try {
    window.sessionStorage.setItem(LOGIN_TRACK_STORAGE_KEY, sessionId)
  } catch (error) {
    console.error('Unable to persist instructor login tracking state:', error)
  }
}

function removeLoginState() {
  if (!canUseSessionStorage()) return

  try {
    window.sessionStorage.removeItem(LOGIN_TRACK_STORAGE_KEY)
  } catch (error) {
    console.error('Unable to clear instructor login tracking state:', error)
  }
}

function compactObject(payload = {}) {
  return Object.entries(payload).reduce((accumulator, [key, value]) => {
    if (value === undefined || value === null || value === '') return accumulator
    accumulator[key] = value
    return accumulator
  }, {})
}

export function getInstructorSession(userId) {
  const existingSession = readSessionState()

  if (existingSession?.userId === userId && existingSession?.sessionId) {
    return existingSession
  }

  const sessionState = {
    userId,
    sessionId: createSessionId(userId),
    startedAtMs: Date.now(),
  }

  writeSessionState(sessionState)
  removeLoginState()
  return sessionState
}

function buildLocationPayload(geo = {}) {
  return compactObject({
    city: geo.city,
    region: geo.region,
    country: geo.country,
    timezone: geo.timezone,
  })
}

function buildBasePayload({ userId, profile = {}, path = '', routeName = '', geo = {}, metadata = {} }) {
  const session = getInstructorSession(userId)

  return {
    sessionId: session.sessionId,
    startedAtMs: session.startedAtMs,
    payload: compactObject({
      sessionId: session.sessionId,
      instructorId: userId,
      instructorName: profile.name || profile.employeeName || profile.fullName,
      instructorEmail: profile.email,
      jobPosition: profile.jobPosition,
      path,
      routeName,
      platform: 'instructor-platform',
      source: 'web',
      createdAt: serverTimestamp(),
      createdAtMs: Date.now(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ...buildLocationPayload(geo),
      metadata: Object.keys(metadata || {}).length ? metadata : undefined,
    }),
  }
}

async function upsertSessionDocument({ sessionId, userId, profile = {}, path = '', routeName = '', geo = {} }) {
  const sessionRef = doc(db, 'instructorActivitySessions', sessionId)
  const locationPayload = buildLocationPayload(geo)

  await setDoc(sessionRef, compactObject({
    sessionId,
    instructorId: userId,
    instructorName: profile.name || profile.employeeName || profile.fullName,
    instructorEmail: profile.email,
    jobPosition: profile.jobPosition,
    platform: 'instructor-platform',
    source: 'web',
    startedAt: serverTimestamp(),
    startedAtMs: Date.now(),
    lastSeenAt: serverTimestamp(),
    lastSeenAtMs: Date.now(),
    lastPath: path,
    lastRouteName: routeName,
    ...locationPayload,
  }), { merge: true })
}

export async function logInstructorActivity({
  userId,
  profile,
  eventType,
  path = '',
  routeName = '',
  geo = {},
  metadata = {},
}) {
  if (!userId || !eventType) return

  const { sessionId, payload } = buildBasePayload({
    userId,
    profile,
    path,
    routeName,
    geo,
    metadata,
  })

  try {
    await upsertSessionDocument({ sessionId, userId, profile, path, routeName, geo })
    await addDoc(collection(db, 'instructorActivityLogs'), {
      ...payload,
      eventType,
    })
  } catch (error) {
    console.error(`Unable to log instructor activity: ${eventType}`, error)
  }
}

export async function logInstructorLogin(details) {
  const session = getInstructorSession(details.userId)
  const lastTrackedLoginSessionId = readLoginState()

  if (lastTrackedLoginSessionId === session.sessionId) {
    return
  }

  await logInstructorActivity({
    ...details,
    eventType: 'login',
  })

  writeLoginState(session.sessionId)
}

export async function logInstructorLogout(details) {
  await logInstructorActivity({
    ...details,
    eventType: 'logout',
  })
}

